import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { AccessibleScope, OperationsStatus, SessionAccess } from '@munifocus/contracts-gov';
import type { Permission } from '@munifocus/access';
import { useSessionAccess } from '../lib/administration-api';
import { useOperationsStatus } from '../lib/operations-api';
import { useAuth } from './AuthProvider';

/** Remembered between visits, so a shared regional officer lands where they left. */
const ACTIVE_SCOPE_KEY = 'gov.activeScope';

/**
 * Forget the remembered scope. Called on sign-out.
 *
 * The scope is remembered per browser, not per account, so without this the
 * next person to sign in on the same machine asks the API for the previous
 * account's municipality or company. The API refuses to answer for a scope the
 * caller does not hold, so the failure is contained — but it still costs a
 * wasted round trip and a first paint at the wrong place.
 */
export function forgetActiveScope(): void {
  try {
    localStorage.removeItem(ACTIVE_SCOPE_KEY);
  } catch {
    // Storage unavailable; there was nothing remembered to forget.
  }
}

interface ScopeContextValue {
  /** Every municipality and company the account can work in. */
  scopes: AccessibleScope[];
  /** The one currently selected, or null before the session resolves. */
  active: AccessibleScope | null;
  setActive: (scope: AccessibleScope) => void;
  /** True when the account works in more than one place. */
  needsSwitcher: boolean;
  /**
   * Does the account hold this permission in the active scope?
   *
   * For deciding what to *show*. Never for deciding what is allowed — the API
   * re-checks every call, so getting this wrong costs a dead menu item, not
   * access. Treat a `false` here as "hide the button", never as security.
   */
  can: (permission: Permission) => boolean;
  isLoading: boolean;
  /**
   * Where the account can use contractor operations, across every scope —
   * null until known, and whenever the module is switched on nowhere it can
   * reach (the default), in which case the operations screens are not offered.
   */
  operations?: OperationsStatus | null;
  /** True while the operations status is still being fetched. */
  operationsLoading?: boolean;
}

const ScopeContext = createContext<ScopeContextValue | null>(null);

function readStored(): { kind: string; id: string } | null {
  try {
    const raw = localStorage.getItem(ACTIVE_SCOPE_KEY);
    return raw ? (JSON.parse(raw) as { kind: string; id: string }) : null;
  } catch {
    // A private window, cleared storage, or a browser blocking site data. The
    // portal works without a remembered scope; it just starts at the first one.
    return null;
  }
}

/**
 * Which municipality or company the officer is currently working in.
 *
 * The old portal had no such concept because there was nothing to switch
 * between — every account saw every file. Now that a person can hold seats at
 * several municipalities and companies, the active scope decides what the file
 * list, the dashboard and the map show, and which permissions gate the
 * navigation.
 */
export function ScopeProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated, user } = useAuth();
  const [selected, setSelected] = useState<{ kind: string; id: string } | null>(readStored);

  // Fetched for the selected scope so `permissions` describes where the officer
  // actually is. The first load has no selection and the API picks a default.
  const session = useSessionAccess(selected?.kind, selected?.id, user?.id);
  const data: SessionAccess | undefined = isAuthenticated ? session.data : undefined;

  const scopes = useMemo(() => data?.scopes ?? [], [data]);

  const active = useMemo(() => {
    if (!scopes.length) return null;
    const match = scopes.find((s) => s.kind === selected?.kind && s.id === selected?.id);
    // A remembered scope the account no longer holds — an appointment that
    // ended, a contract council terminated — silently falls back rather than
    // leaving the portal pointed at somewhere it cannot load.
    return match ?? scopes[0];
  }, [scopes, selected]);

  useEffect(() => {
    if (!active) return;
    try {
      localStorage.setItem(ACTIVE_SCOPE_KEY, JSON.stringify({ kind: active.kind, id: active.id }));
    } catch {
      // Storage unavailable; the selection still holds for this session.
    }
  }, [active]);

  const setActive = useCallback((scope: AccessibleScope) => {
    setSelected({ kind: scope.kind, id: scope.id });
  }, []);

  // Account-wide, not per scope: a contractor's books are kept at the company
  // while its dispatch happens at a town, so the answer does not move with
  // the switcher.
  const operationsStatus = useOperationsStatus(isAuthenticated ? user?.id : null);
  const operations = isAuthenticated ? (operationsStatus.data ?? null) : null;

  const permissions = useMemo(() => new Set(data?.permissions ?? []), [data]);
  const can = useCallback((permission: Permission) => permissions.has(permission), [permissions]);

  const value = useMemo(
    () => ({
      scopes,
      active,
      setActive,
      needsSwitcher: Boolean(data?.needsScopeSwitcher),
      can,
      isLoading: isAuthenticated && session.isLoading,
      operations,
      operationsLoading: isAuthenticated && operationsStatus.isLoading,
    }),
    [
      scopes,
      active,
      setActive,
      data,
      can,
      isAuthenticated,
      session.isLoading,
      operations,
      operationsStatus.isLoading,
    ],
  );

  return <ScopeContext.Provider value={value}>{children}</ScopeContext.Provider>;
}

export function useScope(): ScopeContextValue {
  const ctx = useContext(ScopeContext);
  if (!ctx) throw new Error('useScope must be used inside a ScopeProvider');
  return ctx;
}

/**
 * The scope context, or null when there is no provider above.
 *
 * For chrome that is rendered in both worlds — the shell appears in screens and
 * in isolated component tests, and a shell with no scope context has nothing to
 * switch between, which is a state to render rather than an error to throw.
 * Screens that genuinely depend on a scope keep using {@link useScope}, so a
 * missing provider there still fails loudly.
 */
export function useOptionalScope(): ScopeContextValue | null {
  return useContext(ScopeContext);
}
